import { useEffect, useMemo, useState } from "react";
import MatrixItemSheet from "@/components/MatrixItemSheet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Filter, Plus, Trash2, Pencil, Sparkles, BookOpen, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Library } from "@/lib/api";
import { usePlan } from "@/components/FeatureGate";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import MatrixRationalTab from "@/components/MatrixRationalTab";

const TYPE_LABELS = { clinical: "Clínica (sessão)", home: "Casa (exercício)" };
const PHASE_LABELS = { 1: "Fase 1 · Normalização", 2: "Fase 2 · Correção", 3: "Fase 3 · Integração" };
const GROUP_LABELS = { mobility: "Mobilidade", stretching: "Alongamentos", strengthening: "Fortalecimento", postural: "Correção postural" };
const DIFFICULTY_LABELS = { iniciante: "Iniciante", intermedio: "Intermédio", avancado: "Avançado" };
const HOC_LABELS = { home: "Casa", clinic: "Clínica", both: "Ambos" };
const PROGRESSION_LABELS = { aguda: "Fase aguda", subaguda: "Subaguda", recuperacao: "Recuperação", manutencao: "Manutenção" };

const SECTION_LABELS = {
  principles: "Princípios",
  pillars: "Pilares",
  chains: "Cadeias",
  patterns: "Padrões",
  tests: "Testes",
};

export default function LibraryPage() {
  const [viewing, setViewing] = useState(null);
  const { has } = usePlan();
  const canEdit = has("library_edit");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", type: "", phase: "", group: "", category: "" });
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sp] = useSearchParams();
  const nav = useNavigate();
  // Quando vem de "Ver no Matrix" (?section=&id=) abrimos a Matriz Racional.
  const initialTab = sp.get("section") ? "rational" : "rational";
  // Contexto de retorno (vem da Análise Clínica do workspace).
  const fromWorkspace = sp.get("from") === "workspace";
  const backAid = sp.get("aid");
  const sectionParam = sp.get("section");

  const goBackToWorkspace = () => {
    if (!backAid) { nav(-1); return; }
    nav(`/assessments/${backAid}`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
      const data = await Library.list(params);
      setItems(data);
    } catch { toast.error("Não foi possível carregar a biblioteca"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const categories = useMemo(() => {
    const s = new Set();
    items.forEach(i => i.category && s.add(i.category));
    return Array.from(s).sort();
  }, [items]);

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="library-page">
      {/* Botão sticky + breadcrumb — apenas quando se chega via "Ver no Matrix" da Análise Clínica. */}
      {fromWorkspace && (
        <div
          data-testid="matrix-back-bar"
          className="sticky top-0 z-30 -mx-2 sm:-mx-4 px-2 sm:px-4 py-2.5 backdrop-blur-md bg-slate-950/80 border-b border-sky-500/20 flex items-center justify-between gap-3 rounded-b-xl"
        >
          <button
            type="button"
            onClick={goBackToWorkspace}
            data-testid="matrix-back-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 hover:border-sky-400/60 transition text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar à Análise Clínica
          </button>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-mono text-slate-400">
            <span className="text-slate-500">Análise Clínica</span>
            <span className="text-slate-600">→</span>
            <span className="text-slate-300">MIO Matrix</span>
            {sectionParam && SECTION_LABELS[sectionParam] && (
              <>
                <span className="text-slate-600">→</span>
                <span className="text-sky-300">{SECTION_LABELS[sectionParam]}</span>
              </>
            )}
          </div>
        </div>
      )}

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Recursos</div>
          <h1 className="font-display text-4xl text-slate-50 mt-2 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-sky-400" strokeWidth={1.5} /> MIO Matrix Library
          </h1>
          <p className="text-slate-400 text-sm mt-1">Sistema osteopático modular — racional clínico, padrões adaptativos e biblioteca funcional integrada.</p>
        </div>
      </header>

      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="bg-slate-900/70 p-1 rounded-full border border-slate-800 inline-flex gap-1">
          <TabsTrigger value="rational"  data-testid="matrix-tab-rational"  className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950 px-4">Matriz Racional</TabsTrigger>
          <TabsTrigger value="functional" data-testid="matrix-tab-functional" className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950 px-4">Matriz Funcional</TabsTrigger>
        </TabsList>

        <TabsContent value="rational" className="mt-6">
          <MatrixRationalTab initialSection={sp.get("section")} initialId={sp.get("id")} />
        </TabsContent>

        <TabsContent value="functional" className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Biblioteca funcional · técnicas e exercícios</div>
            <Button data-testid="add-library-btn" onClick={() => setOpenNew(true)} disabled={!canEdit} title={canEdit ? "" : "Funcionalidade incluída no plano Pro ou Premium"} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full glow-sky disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-500">
              <Plus className="w-4 h-4 mr-2" /> Novo item
            </Button>
          </div>

      {!canEdit && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-200/80" data-testid="library-readonly-banner">
          Estás a consultar a biblioteca em modo <strong>leitura</strong>. Para adicionar, editar ou remover itens personalizados, atualiza para o plano <strong>Pro</strong> ou <strong>Premium</strong>.
        </div>
      )}

      {/* Filtros */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
          <Filter className="w-3.5 h-3.5" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="library-search"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Pesquisar técnica, objetivo ou região corporal…"
              className="pl-9 bg-slate-950 border-slate-800 text-slate-100 placeholder:text-slate-500"
            />
          </div>
          <select data-testid="filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value, phase: "", group: "" })}
                  className="h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm text-slate-100">
            <option value="">Tipo: todos</option>
            <option value="clinical">Clínica (sessão)</option>
            <option value="home">Casa (exercício)</option>
          </select>
          {filters.type !== "home" && (
            <select data-testid="filter-phase" value={filters.phase} onChange={(e) => setFilters({ ...filters, phase: e.target.value })}
                    className="h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm text-slate-100">
              <option value="">Fase: todas</option>
              <option value="1">Fase 1</option>
              <option value="2">Fase 2</option>
              <option value="3">Fase 3</option>
            </select>
          )}
          {filters.type !== "clinical" && (
            <select data-testid="filter-group" value={filters.group} onChange={(e) => setFilters({ ...filters, group: e.target.value })}
                    className="h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm text-slate-100">
              <option value="">Grupo: todos</option>
              <option value="mobility">Mobilidade</option>
              <option value="stretching">Alongamentos</option>
              <option value="strengthening">Fortalecimento</option>
              <option value="postural">Postura</option>
            </select>
          )}
        </div>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2">
            <button onClick={() => setFilters({ ...filters, category: "" })} className={`text-[11px] px-2.5 py-1 rounded-full border ${!filters.category ? "border-sky-500/40 bg-sky-500/10 text-sky-200" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>
              Todas
            </button>
            {categories.map(c => (
              <button key={c} onClick={() => setFilters({ ...filters, category: c })} className={`text-[11px] px-2.5 py-1 rounded-full border ${filters.category === c ? "border-sky-500/40 bg-sky-500/10 text-sky-200" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>
                {c}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Resultados */}
      {loading ? (
        <div className="text-slate-500">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center text-slate-500 bg-slate-900/30">
          Sem resultados para estes filtros.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(it => (
            <LibraryCard key={it.id} item={it} canEdit={canEdit} onEdit={() => setEditing(it)} onDeleted={load} onOpen={() => setViewing(it)} />
          ))}
        </div>
      )}

      <LibraryItemDialog
        open={openNew}
        onOpenChange={setOpenNew}
        onSaved={() => { setOpenNew(false); load(); }}
      />
      <LibraryItemDialog
        open={!!editing}
        item={editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        onSaved={() => { setEditing(null); load(); }}
      />
      <MatrixItemSheet item={viewing} onClose={() => setViewing(null)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LibraryCard({ item, canEdit, onEdit, onDeleted, onOpen }) {
  const { user } = useAuth();
  const manage = canEdit && (!item.is_seed || (item.pack && user?.role === "admin"));
  const toggleHidden = async () => {
    try { await Library.update(item.id, { hidden: !item.hidden }); toast.success(item.hidden ? "Item visível na biblioteca" : "Item ocultado"); onDeleted?.(); }
    catch { toast.error("Não foi possível atualizar o item"); }
  };
  const badge = item.type === "home" ? "Casa" : "Clínica";
  const badgeColor = item.type === "home" ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-sky-500/30 bg-sky-500/10 text-sky-300";

  const remove = async () => {
    if (!window.confirm(`Remover "${item.name}"?`)) return;
    try { await Library.remove(item.id); toast.success("Item removido"); onDeleted?.(); }
    catch (e) {
      const msg = e?.response?.status === 403 ? "Itens globais não podem ser removidos" : "Não foi possível remover";
      toast.error(msg);
    }
  };

  return (
    <div data-testid={`library-card-${item.id}`} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 hover:border-sky-500/30 transition">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badgeColor} uppercase tracking-wider`}>{badge}</span>
            {item.phase && <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">{PHASE_LABELS[item.phase]}</span>}
            {item.group && <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">{GROUP_LABELS[item.group] || item.group}</span>}
            {item.difficulty && <span className="text-[10px] px-2 py-0.5 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300" data-testid="badge-difficulty">{DIFFICULTY_LABELS[item.difficulty] || item.difficulty}</span>}
            {item.duration_minutes && <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400" data-testid="badge-duration">{item.duration_minutes} min</span>}
            {item.home_or_clinic && <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">{HOC_LABELS[item.home_or_clinic] || item.home_or_clinic}</span>}
            {item.progression_phase && <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">{PROGRESSION_LABELS[item.progression_phase] || item.progression_phase}</span>}
            {item.is_seed && <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">Global</span>}
            {item.professional_only && <span className="text-[10px] px-2 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300" data-testid={`badge-pro-only-${item.id}`}>Só profissional</span>}
            {item.hidden && <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-600 text-slate-400" data-testid={`badge-hidden-${item.id}`}>Oculto</span>}
          </div>
          <button type="button" onClick={onOpen} data-testid={`library-open-${item.id}`} className="text-left text-slate-50 font-medium mt-2 hover:text-sky-300 underline-offset-4 hover:underline transition-colors">{item.name}</button>
          <div className="text-xs text-sky-300 mt-0.5">{item.category}</div>
          {item.body_region && <div className="text-[11px] text-slate-400 mt-0.5">Região: <span className="text-slate-200">{item.body_region}</span></div>}
          {item.therapeutic_goal && <div className="text-[11px] text-slate-400 mt-0.5">Meta terapêutica: <span className="text-slate-200">{item.therapeutic_goal}</span></div>}
          {item.repetitions && <div className="text-[11px] text-slate-400 mt-0.5">Doseamento: <span className="text-slate-200">{item.repetitions}</span></div>}
          {item.equipment?.length > 0 && (
            <div className="text-[11px] text-slate-400 mt-0.5">Material: <span className="text-slate-200">{item.equipment.join(", ")}</span></div>
          )}
          {item.contraindications?.length > 0 && (
            <div className="text-[11px] text-amber-400/80 mt-0.5">Precauções: <span className="text-amber-200">{item.contraindications.join(" · ")}</span></div>
          )}
          {item.description && <div className="text-sm text-slate-300 mt-2">{item.description}</div>}
          {item.objective && <div className="text-xs text-emerald-400 mt-2"><span className="text-slate-500">Objetivo: </span>{item.objective}</div>}
          {item.instructions && item.instructions !== item.description && <div className="text-[11px] text-slate-400 mt-2 italic">Instruções: {item.instructions}</div>}
        </div>
        {manage && (
          <div className="flex flex-col gap-1.5">
            <button data-testid={`edit-item-${item.id}`} onClick={onEdit} className="p-1.5 text-slate-500 hover:text-sky-300"><Pencil className="w-3.5 h-3.5" /></button>
            {item.pack && <button data-testid={`hide-item-${item.id}`} onClick={toggleHidden} title={item.hidden ? "Mostrar" : "Ocultar"} className="p-1.5 text-slate-500 hover:text-amber-300">{item.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>}
            <button data-testid={`delete-item-${item.id}`} onClick={remove} className="p-1.5 text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function LibraryItemDialog({ open, item, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(item ? {
      ...item,
      equipment: Array.isArray(item.equipment) ? item.equipment.join(", ") : (item.equipment || ""),
      contraindications: Array.isArray(item.contraindications) ? item.contraindications.join(", ") : (item.contraindications || ""),
    } : {
      name: "", category: "", description: "", instructions: "",
      body_region: "", objective: "", type: "clinical", phase: null, group: null,
      difficulty: "", duration_minutes: "", repetitions: "",
      equipment: "", contraindications: "",
      therapeutic_goal: "", home_or_clinic: "", progression_phase: "",
      video_url: "", image_url: "",
    });
  }, [item, open]);

  if (!form) return null;

  const splitList = (s) => (s || "").split(",").map((v) => v.trim()).filter(Boolean);

  const save = async () => {
    if (!form.name || !form.category) { toast.error("Nome e categoria são obrigatórios"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name, category: form.category,
        description: form.description, instructions: form.instructions, limitations: form.limitations || "",
        body_region: form.body_region, objective: form.objective,
        type: form.type,
        phase: form.type === "clinical" ? (form.phase ? Number(form.phase) : null) : null,
        group: form.type === "home" ? (form.group || null) : null,
        // Metadados avançados
        difficulty: form.difficulty || null,
        duration_minutes: form.duration_minutes === "" || form.duration_minutes == null ? null : Number(form.duration_minutes),
        repetitions: form.repetitions || null,
        equipment: splitList(form.equipment),
        contraindications: splitList(form.contraindications),
        therapeutic_goal: form.therapeutic_goal || null,
        home_or_clinic: form.home_or_clinic || null,
        progression_phase: form.progression_phase || null,
        video_url: form.video_url || null,
        image_url: form.image_url || null,
      };
      if (item?.id) { await Library.update(item.id, payload); toast.success("Item atualizado"); }
      else          { await Library.create(payload);     toast.success("Item adicionado"); }
      onSaved?.();
    } catch { toast.error("Não foi possível guardar"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto overscroll-contain bg-slate-950 border-slate-800 text-slate-100" data-testid="library-item-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 font-display">
            <Sparkles className="w-4 h-4 text-sky-400 inline mr-2" />
            {item ? "Editar item" : "Novo item da biblioteca"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">Personaliza técnicas e exercícios para reutilizares nos teus planos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Tipo</Label>
              <select className="w-full h-10 bg-slate-900 border border-slate-800 rounded-md px-3 text-sm text-slate-100 mt-1"
                      data-testid="form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, phase: null, group: null })}>
                <option value="clinical">Clínica (sessão)</option>
                <option value="home">Casa (exercício)</option>
              </select>
            </div>
            {form.type === "clinical" ? (
              <div>
                <Label className="text-slate-300">Fase</Label>
                <select className="w-full h-10 bg-slate-900 border border-slate-800 rounded-md px-3 text-sm text-slate-100 mt-1"
                        data-testid="form-phase" value={form.phase ?? ""} onChange={(e) => setForm({ ...form, phase: e.target.value })}>
                  <option value="">—</option>
                  <option value="1">Fase 1</option>
                  <option value="2">Fase 2</option>
                  <option value="3">Fase 3</option>
                </select>
              </div>
            ) : (
              <div>
                <Label className="text-slate-300">Grupo</Label>
                <select className="w-full h-10 bg-slate-900 border border-slate-800 rounded-md px-3 text-sm text-slate-100 mt-1"
                        data-testid="form-group" value={form.group ?? ""} onChange={(e) => setForm({ ...form, group: e.target.value })}>
                  <option value="">—</option>
                  <option value="mobility">Mobilidade</option>
                  <option value="stretching">Alongamentos</option>
                  <option value="strengthening">Fortalecimento</option>
                  <option value="postural">Postura</option>
                </select>
              </div>
            )}
          </div>
          <div>
            <Label className="text-slate-300">Nome *</Label>
            <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Categoria *</Label>
              <Input data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" placeholder="Ex.: HVLA, Mobilização, Alongamento…" />
            </div>
            <div>
              <Label className="text-slate-300">Região corporal</Label>
              <Input data-testid="form-region" value={form.body_region} onChange={(e) => setForm({ ...form, body_region: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Objetivo</Label>
            <Input data-testid="form-objective" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Descrição</Label>
            <Textarea data-testid="form-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Instruções</Label>
            <Textarea data-testid="form-instructions" rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1" />
            <Textarea data-testid="form-limitations" rows={2} placeholder="Limitações relevantes" value={form.limitations || ""} onChange={(e) => setForm({ ...form, limitations: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-2" />
          </div>

          {/* Metadados avançados (P0 - Roadmap Fase 1) */}
          <div className="pt-3 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Metadados avançados</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Dificuldade</Label>
                <select className="w-full h-9 bg-slate-900 border border-slate-800 rounded-md px-2 text-sm text-slate-100 mt-1"
                        data-testid="form-difficulty" value={form.difficulty || ""} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
                  <option value="">—</option>
                  <option value="iniciante">Iniciante</option>
                  <option value="intermedio">Intermédio</option>
                  <option value="avancado">Avançado</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Duração (min)</Label>
                <Input type="number" min="0" data-testid="form-duration" value={form.duration_minutes ?? ""} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 h-9" placeholder="10" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Doseamento</Label>
                <Input data-testid="form-repetitions" value={form.repetitions || ""} onChange={(e) => setForm({ ...form, repetitions: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 h-9" placeholder="3x10 ou 30s/lado" />
              </div>
              <div className="col-span-3">
                <Label className="text-slate-300 text-xs">Material (separar por vírgula)</Label>
                <Input data-testid="form-equipment" value={form.equipment || ""} onChange={(e) => setForm({ ...form, equipment: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 h-9" placeholder="bola suíça, elástico, rolo" />
              </div>
              <div className="col-span-3">
                <Label className="text-slate-300 text-xs">Precauções/Contraindicações funcionais (vírgula)</Label>
                <Input data-testid="form-contraindications" value={form.contraindications || ""} onChange={(e) => setForm({ ...form, contraindications: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 h-9" placeholder="dor aguda, vertigem ao decúbito" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Meta terapêutica</Label>
                <Input data-testid="form-goal" value={form.therapeutic_goal || ""} onChange={(e) => setForm({ ...form, therapeutic_goal: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 h-9" placeholder="mobilidade, estabilização…" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Contexto</Label>
                <select className="w-full h-9 bg-slate-900 border border-slate-800 rounded-md px-2 text-sm text-slate-100 mt-1"
                        data-testid="form-hoc" value={form.home_or_clinic || ""} onChange={(e) => setForm({ ...form, home_or_clinic: e.target.value })}>
                  <option value="">—</option>
                  <option value="home">Casa</option>
                  <option value="clinic">Clínica</option>
                  <option value="both">Ambos</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Fase progressão</Label>
                <select className="w-full h-9 bg-slate-900 border border-slate-800 rounded-md px-2 text-sm text-slate-100 mt-1"
                        data-testid="form-progression" value={form.progression_phase || ""} onChange={(e) => setForm({ ...form, progression_phase: e.target.value })}>
                  <option value="">—</option>
                  <option value="aguda">Aguda</option>
                  <option value="subaguda">Subaguda</option>
                  <option value="recuperacao">Recuperação</option>
                  <option value="manutencao">Manutenção</option>
                </select>
              </div>
              <div className="col-span-3 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">URL de vídeo</Label>
                  <Input data-testid="form-video-url" value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 h-9" placeholder="https://…" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">URL de imagem</Label>
                  <Input data-testid="form-image-url" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-slate-900 border-slate-800 text-slate-50 mt-1 h-9" placeholder="https://…" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-3 bg-slate-950/95 border-t border-slate-800 gap-2">
          <Button variant="ghost" className="text-slate-400" onClick={() => onOpenChange?.(false)} data-testid="form-cancel-btn">Cancelar</Button>
          <Button data-testid="form-save-btn" onClick={save} disabled={busy} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6">
            {busy ? "A guardar…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
